import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient, { axiosClientOrders } from "../axios/api.js";

// ─── Fetch orders list (with optional search) ─────────────────────────────
export const fetchOrders = createAsyncThunk(
  "orders/fetchOrders",
  async (searchValue = "", { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get("/orders", {
        params: { search: searchValue },
      });
      if (response.data && response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch orders.");
    }
  }
);

// ─── Fetch a single order by id — used by OrderModelForm (edit/reset) ─────
export const fetchOrderById = createAsyncThunk(
  "orders/fetchOrderById",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get(`/orders/${orderId}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load order data");
    }
  }
);

// ─── Fetch a single order by id — used by Order.jsx action modal ──────────
export const fetchOrderForAction = createAsyncThunk(
  "orders/fetchOrderForAction",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get(`/orders/${orderId}`);
      return response?.data?.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load order actions");
    }
  }
);

// ─── Create order ──────────────────────────────────────────────────────────
export const createOrder = createAsyncThunk(
  "orders/createOrder",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.post("/orders", payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to save order. Please try again."
      );
    }
  }
);

// ─── Update order ───────────────────────────────────────────────────────────
export const updateOrder = createAsyncThunk(
  "orders/updateOrder",
  async ({ orderId, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.put(`/orders/orders-update/${orderId}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to save order. Please try again."
      );
    }
  }
);

// ─── Status-only update (then refresh the list with the current search
// term) — used to auto-flip a Pending order to Completed once its
// logistics-doc progress ring hits 100%. Unlike updateOrder, this never
// touches any other field on the order.
export const updateOrderStatus = createAsyncThunk(
  "orders/updateOrderStatus",
  async ({ id, status, searchTerm = "" }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosClientOrders.put(`/orders/${id}/status`, { status });
      await dispatch(fetchOrders(searchTerm));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update order status.");
    }
  }
);

// ─── Delete order (then refresh the list with the current search term) ────
export const deleteOrder = createAsyncThunk(
  "orders/deleteOrder",
  async ({ id, searchTerm = "" }, { rejectWithValue, dispatch }) => {
    try {
      await axiosClientOrders.delete(`/orders/${id}`);
      await dispatch(fetchOrders(searchTerm));
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete order.");
    }
  }
);

// ─── Fetch quotation total for a given RFQ + currency ──────────────────────
export const fetchQuotationTotal = createAsyncThunk(
  "orders/fetchQuotationTotal",
  async ({ rfqNo, currency }, { rejectWithValue }) => {
    if (!rfqNo || rfqNo.trim() === "") return null;
    try {
      const response = await axiosClientOrders.get(
        `/orders/quotation-total/${rfqNo}/${currency}`
      );
      if (response.data.success && response.data.data) {
        let totalAmount = parseFloat(response.data.data.total_amount);
        if (isNaN(totalAmount)) totalAmount = 0;
        return Math.round(totalAmount * 100) / 100;
      }
      return 0;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error fetching quotation total");
    }
  }
);

// Fetch the lead/inquiry record for an RFQ from the `solitaire` table
// (client_name, mobile_number, location, ...) — used to auto-fill Buyer/
// Consignee info on the order form.
export const fetchInquiryDetails = createAsyncThunk(
  "orders/fetchInquiryDetails",
  async (rfqNo, { rejectWithValue }) => {
    if (!rfqNo || rfqNo.trim() === "") return null;
    try {
      const response = await axiosClient.get("/api/inquiry-details", {
        params: { rfq_no: rfqNo },
      });
      return response.data && response.data.length > 0 ? response.data[0] : null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error fetching inquiry details");
    }
  }
);

// ─── Quotation bundle: materials + vendors + vendor count + terms + inquiry ─
// Mirrors the original fetchQuotationData() orchestration in QuotationModel.jsx:
// each sub-fetch is fault-isolated (a single failed piece doesn't abort the rest).
export const fetchQuotationBundle = createAsyncThunk(
  "orders/fetchQuotationBundle",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const materialResponse = await axiosClientOrders.get("/api/order-quotation-material", {
        params: { rfq_no },
      });
      if (materialResponse.data.error) {
        throw new Error(materialResponse.data.error);
      }

      let vendorData = [];
      try {
        const vendorRes = await axiosClientOrders.get("/api/quotation-vendor", { params: { rfq_no } });
        vendorData = vendorRes.data;
      } catch (error) {
        console.error("Error fetching vendor data:", error);
      }

      let vendorCount = 0;
      try {
        const vendorCountRes = await axiosClientOrders.get("/api/quotation-vendor-count", { params: { rfq_no } });
        vendorCount = vendorCountRes.data;
      } catch (error) {
        console.error("Error fetching vendor count:", error);
      }

      // Every vendor ASSIGNED to this RFQ (main backend, `vendor_rfq` table) —
      // includes vendors that never submitted a rate, so the quotation can
      // still show a column for them with a "RATE NOT GIVEN" tag. Uses the
      // default axiosClient (port 3000), not axiosClientOrders.
      let vendorNameList = [];
      try {
        const vnRes = await axiosClient.post("/api/vendor-name", { rfq_no });
        const raw = vnRes.data;
        vendorNameList = Array.isArray(raw)
          ? raw
              .map((x) => (typeof x === "string" ? x : x?.vendor_name || x?.vname || x?.name || ""))
              .map((s) => String(s || "").trim())
              .filter(Boolean)
          : [];
      } catch (error) {
        console.error("Error fetching assigned vendor names:", error);
      }

      let termsConditionData = [];
      try {
        const termsRes = await axiosClientOrders.get("/api/quotation-terms-condition", { params: { rfq_no } });
        termsConditionData = termsRes.data;
      } catch (error) {
        console.error("Error fetching terms conditions:", error);
      }

      let inquiryDetails = null;
      try {
        const inquiryRes = await axiosClientOrders.get("/api/inquiry-details", {
          params: { rfq_no },
        });
        if (inquiryRes.data.length > 0) {
          inquiryDetails = inquiryRes.data[0];
        }
      } catch (error) {
        console.error("Error fetching contact details:", error);
      }

      return {
        quotationMaterial: materialResponse.data,
        vendorData,
        vendorCount,
        vendorNameList,
        termsConditionData,
        inquiryDetails,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load quotation data");
    }
  }
);

export const saveQuotationMaterials = createAsyncThunk(
  "orders/saveQuotationMaterials",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.put("/api/order-quotation-update", payload);
      return { status: response.status, data: response.data };
    } catch (error) {
      const d = error.response?.data;
      let msg = d?.error || d?.message || error.message || "Save failed";
      // The quotation endpoint validates with Joi and returns every failing
      // field in `errors: [{ field, message }]` — surface those so the real
      // cause is visible instead of a bare "status code 400".
      if (Array.isArray(d?.errors) && d.errors.length) {
        msg += " — " + d.errors.map((e) => `${e.field}: ${e.message}`).join("; ");
      }
      return rejectWithValue(msg);
    }
  }
);

export const saveQuotationTerms = createAsyncThunk(
  "orders/saveQuotationTerms",
  async ({ rfq_no, terms }, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.put("/api/order-terms-conditions-update", { rfq_no, terms });
      return { status: response.status, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ─── PO Copies ──────────────────────────────────────────────────────────────
export const fetchPoCopies = createAsyncThunk(
  "orders/fetchPoCopies",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get(`/api/po-copies/${orderId}`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch PO copies");
    }
  }
);

export const createPoCopy = createAsyncThunk(
  "orders/createPoCopy",
  async ({ formDataToSend, onUploadProgress }, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.post("/api/po-copies/upload", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
      });
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to save PO copy");
    }
  }
);

export const updatePoCopy = createAsyncThunk(
  "orders/updatePoCopy",
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.put(`/api/po-copies/${id}`, updateData);
      if (response.data.success) {
        return response.data;
      }
      throw new Error(response.data.error);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to save PO copy");
    }
  }
);

export const deletePoCopy = createAsyncThunk(
  "orders/deletePoCopy",
  async ({ id, orderId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosClientOrders.delete(`/api/po-copies/${id}`);
      if (response.data.success) {
        await dispatch(fetchPoCopies(orderId));
        return id;
      }
      throw new Error(response.data.error);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to delete PO copy");
    }
  }
);

// ─── Signed URL for previewing S3 attachments (reused across order docs) ───
export const fetchSignedUrl = createAsyncThunk(
  "orders/fetchSignedUrl",
  async (s3Url, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get("/api/proforma-invoices/signed-url", {
        params: { url: encodeURIComponent(s3Url), expires_in: 3600 },
      });
      return response.data.signedUrl;
    } catch (error) {
      return rejectWithValue(error.message || "Could not open file.");
    }
  }
);

// ─── Order documents (Quality / Purchase / Logistics — shared by apiBase) ──
export const fetchOrderDocs = createAsyncThunk(
  "orders/fetchOrderDocs",
  async ({ apiBase, orderId }, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.get(`${apiBase}/${orderId}`);
      return res.data?.data || res.data || [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch documents");
    }
  }
);

export const uploadOrderDoc = createAsyncThunk(
  "orders/uploadOrderDoc",
  async ({ apiBase, formDataToSend, onUploadProgress }, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.post(`${apiBase}/upload`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Upload failed");
    }
  }
);

export const deleteOrderDoc = createAsyncThunk(
  "orders/deleteOrderDoc",
  async ({ apiBase, id }, { rejectWithValue }) => {
    try {
      await axiosClientOrders.delete(`${apiBase}/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue("Delete failed");
    }
  }
);

// Documents for one specific vendor PO, already grouped by doc_name
// (case-insensitive) on the backend — powers the PO No sidebar without any
// client-side filtering of the full order-wide doc list.
export const fetchDocsForPo = createAsyncThunk(
  "orders/fetchDocsForPo",
  async ({ apiBase, orderId, vendorOrderId }, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.get(`${apiBase}/${orderId}/po/${vendorOrderId}`);
      return { data: res.data?.data || [], groupedByType: res.data?.groupedByType || {} };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch PO documents");
    }
  }
);

// ─── Export Value Declaration (Annexure-A) ─────────────────────────────────
export const saveExportValueDeclaration = createAsyncThunk(
  "orders/saveExportValueDeclaration",
  async (formDataToSend, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.post(
        "/api/order-docs/logistics/export-value-declaration",
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Save failed");
    }
  }
);

export const fetchExportValueDeclarationByPo = createAsyncThunk(
  "orders/fetchExportValueDeclarationByPo",
  async (vendorOrderId, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.get(
        `/api/order-docs/logistics/export-value-declaration/po/${vendorOrderId}`
      );
      return res.data?.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch declaration");
    }
  }
);

// ─── Custom Declaration ─────────────────────────────────────────────────────
export const saveCustomDeclaration = createAsyncThunk(
  "orders/saveCustomDeclaration",
  async (formDataToSend, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.post(
        "/api/order-docs/logistics/custom-declaration",
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Save failed");
    }
  }
);

export const fetchCustomDeclarationByPo = createAsyncThunk(
  "orders/fetchCustomDeclarationByPo",
  async (vendorOrderId, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.get(
        `/api/order-docs/logistics/custom-declaration/po/${vendorOrderId}`
      );
      return res.data?.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch declaration");
    }
  }
);

// ─── Drawback Declaration ───────────────────────────────────────────────────
export const saveDrawbackDeclaration = createAsyncThunk(
  "orders/saveDrawbackDeclaration",
  async (formDataToSend, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.post(
        "/api/order-docs/logistics/drawback-declaration",
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Save failed");
    }
  }
);

export const fetchDrawbackDeclarationByPo = createAsyncThunk(
  "orders/fetchDrawbackDeclarationByPo",
  async (vendorOrderId, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.get(
        `/api/order-docs/logistics/drawback-declaration/po/${vendorOrderId}`
      );
      return res.data?.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch declaration");
    }
  }
);

// ─── Custom Invoice (Export Invoice) ────────────────────────────────────────
export const saveCustomInvoice = createAsyncThunk(
  "orders/saveCustomInvoice",
  async (formDataToSend, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.post(
        "/api/order-docs/logistics/custom-invoice",
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      const d = error.response?.data;
      const detail = d?.error || d?.message || (typeof d === "string" ? d : null) || error.message || "Save failed";
      const status = error.response?.status;
      return rejectWithValue(status ? `[HTTP ${status}] ${detail}` : detail);
    }
  }
);

export const fetchCustomInvoiceByPo = createAsyncThunk(
  "orders/fetchCustomInvoiceByPo",
  async (vendorOrderId, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.get(
        `/api/order-docs/logistics/custom-invoice/po/${vendorOrderId}`
      );
      return res.data?.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch invoice");
    }
  }
);

// Line-item structure of every saved Export Invoice for a PO — used by the
// "new invoice" form to skip already-invoiced materials, and by the Packing
// List form to scope its picker to what's actually been invoiced. Mirrors
// fetchPackingListContainers.
// arg is either a plain vendorOrderId (existing callers — defaults to the
// "custom" doc_kind, unchanged behaviour) or { vendorOrderId, docKind } so
// the Commercial Invoice form/records table can scope to its own saved
// records instead of Custom Invoice's.
export const fetchCustomInvoiceLineItems = createAsyncThunk(
  "orders/fetchCustomInvoiceLineItems",
  async (arg, { rejectWithValue }) => {
    const vendorOrderId = typeof arg === "object" && arg !== null ? arg.vendorOrderId : arg;
    const docKind = (typeof arg === "object" && arg !== null && arg.docKind) || "custom";
    try {
      const res = await axiosClientOrders.get(
        `/api/order-docs/logistics/custom-invoice/po/${vendorOrderId}/items`,
        { params: { doc_kind: docKind } }
      );
      return res.data?.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to load invoiced quantities");
    }
  }
);

// All saved Export/Commercial Invoices for a PO — powers the records table.
// Same arg shape as fetchCustomInvoiceLineItems above.
export const fetchCustomInvoiceList = createAsyncThunk(
  "orders/fetchCustomInvoiceList",
  async (arg, { rejectWithValue }) => {
    const vendorOrderId = typeof arg === "object" && arg !== null ? arg.vendorOrderId : arg;
    const docKind = (typeof arg === "object" && arg !== null && arg.docKind) || "custom";
    try {
      const res = await axiosClientOrders.get(
        `/api/order-docs/logistics/custom-invoice/po/${vendorOrderId}/list`,
        { params: { doc_kind: docKind } }
      );
      return res.data?.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to load invoices");
    }
  }
);

// One saved Export Invoice by its id — used to pre-fill the form for editing.
export const fetchCustomInvoiceById = createAsyncThunk(
  "orders/fetchCustomInvoiceById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.get(`/api/order-docs/logistics/custom-invoice/id/${id}`);
      return res.data?.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to load invoice");
    }
  }
);

export const updateCustomInvoice = createAsyncThunk(
  "orders/updateCustomInvoice",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.put(
        `/api/order-docs/logistics/custom-invoice/${id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return res.data;
    } catch (error) {
      const d = error.response?.data;
      const detail = d?.error || d?.message || (typeof d === "string" ? d : null) || error.message || "Update failed";
      const status = error.response?.status;
      return rejectWithValue(status ? `[HTTP ${status}] ${detail}` : detail);
    }
  }
);

export const setCustomInvoiceStatus = createAsyncThunk(
  "orders/setCustomInvoiceStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.patch(
        `/api/order-docs/logistics/custom-invoice/${id}/status`,
        { status }
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Status update failed");
    }
  }
);

// Points a saved invoice at its paired Custom/Commercial Invoice — filling
// in a Custom Invoice also creates/updates a Commercial Invoice from the
// same form data (see CustomInvoiceDialog), and this lets a later edit find
// that paired record instead of creating a duplicate.
export const linkCustomInvoice = createAsyncThunk(
  "orders/linkCustomInvoice",
  async ({ id, linkedInvoiceId }, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.patch(
        `/api/order-docs/logistics/custom-invoice/${id}/link`,
        { linked_invoice_id: linkedInvoiceId }
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Link failed");
    }
  }
);

export const deleteCustomInvoice = createAsyncThunk(
  "orders/deleteCustomInvoice",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.delete(`/api/order-docs/logistics/custom-invoice/${id}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Delete failed");
    }
  }
);

// ─── Packing List ───────────────────────────────────────────────────────────
export const savePackingList = createAsyncThunk(
  "orders/savePackingList",
  async (formDataToSend, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.post(
        "/api/order-docs/logistics/packing-list",
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (error) {
      const d = error.response?.data;
      const detail = d?.error || d?.message || (typeof d === "string" ? d : null) || error.message || "Save failed";
      const status = error.response?.status;
      return rejectWithValue(status ? `[HTTP ${status}] ${detail}` : detail);
    }
  }
);

export const fetchPackingListByPo = createAsyncThunk(
  "orders/fetchPackingListByPo",
  async (vendorOrderId, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.get(
        `/api/order-docs/logistics/packing-list/po/${vendorOrderId}`
      );
      return res.data?.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch packing list");
    }
  }
);

// arg is either a plain vendorOrderId (existing callers — defaults to the
// "custom" doc_kind, unchanged behaviour) or { vendorOrderId, docKind } so
// the Commercial Packing List pairing can scope to its own saved records
// (and never double-count "already packed" quantities against the other).
// All saved Packing/Commercial Packing Lists for a PO — powers the records table.
export const fetchPackingListList = createAsyncThunk(
  "orders/fetchPackingListList",
  async (arg, { rejectWithValue }) => {
    const vendorOrderId = typeof arg === "object" && arg !== null ? arg.vendorOrderId : arg;
    const docKind = (typeof arg === "object" && arg !== null && arg.docKind) || "custom";
    try {
      const res = await axiosClientOrders.get(
        `/api/order-docs/logistics/packing-list/po/${vendorOrderId}/list`,
        { params: { doc_kind: docKind } }
      );
      return res.data?.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to load packing lists");
    }
  }
);

// Container/item structure of every saved Packing List for a PO — used by
// the "new packing list" form to compute how much of each material is
// already packed elsewhere. Same arg shape as fetchPackingListList above.
export const fetchPackingListContainers = createAsyncThunk(
  "orders/fetchPackingListContainers",
  async (arg, { rejectWithValue }) => {
    const vendorOrderId = typeof arg === "object" && arg !== null ? arg.vendorOrderId : arg;
    const docKind = (typeof arg === "object" && arg !== null && arg.docKind) || "custom";
    try {
      const res = await axiosClientOrders.get(
        `/api/order-docs/logistics/packing-list/po/${vendorOrderId}/containers`,
        { params: { doc_kind: docKind } }
      );
      return res.data?.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to load packed quantities");
    }
  }
);

// One saved Packing List by its id — used to pre-fill the form for editing.
export const fetchPackingListById = createAsyncThunk(
  "orders/fetchPackingListById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.get(`/api/order-docs/logistics/packing-list/id/${id}`);
      return res.data?.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to load packing list");
    }
  }
);

export const updatePackingList = createAsyncThunk(
  "orders/updatePackingList",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.put(
        `/api/order-docs/logistics/packing-list/${id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return res.data;
    } catch (error) {
      const d = error.response?.data;
      const detail = d?.error || d?.message || (typeof d === "string" ? d : null) || error.message || "Update failed";
      const status = error.response?.status;
      return rejectWithValue(status ? `[HTTP ${status}] ${detail}` : detail);
    }
  }
);

export const setPackingListStatus = createAsyncThunk(
  "orders/setPackingListStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.patch(
        `/api/order-docs/logistics/packing-list/${id}/status`,
        { status }
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Status update failed");
    }
  }
);

export const deletePackingList = createAsyncThunk(
  "orders/deletePackingList",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.delete(`/api/order-docs/logistics/packing-list/${id}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Delete failed");
    }
  }
);

// Points a saved packing list at its paired Packing/Commercial Packing List
// — filling in a Packing List also creates/updates a Commercial Packing
// List from the same data (see PackingListDialog), and this lets a later
// edit find that paired record instead of creating a duplicate.
export const linkPackingList = createAsyncThunk(
  "orders/linkPackingList",
  async ({ id, linkedInvoiceId }, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.patch(
        `/api/order-docs/logistics/packing-list/${id}/link`,
        { linked_invoice_id: linkedInvoiceId }
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Link failed");
    }
  }
);

// ─── Vendor table (submitted vendor orders, vendors, followups) ───────────
export const fetchVendorsList = createAsyncThunk(
  "orders/fetchVendorsList",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get("/api/quotation-vendor", { params: { rfq_no } });
      if (response.data && response.data.length > 0) {
        return response.data.map((vendor, index) => ({
          id: vendor.id || index + 1,
          name: vendor.vname || `Vendor ${index + 1}`,
          email: vendor.email || "N/A",
          phone: vendor.phone || "N/A",
          status: "Active",
          materials_count: vendor.materials_count || 0,
        }));
      }
      return [];
    } catch (error) {
      return rejectWithValue("Failed to load vendors");
    }
  }
);

export const fetchSubmittedVendorOrders = createAsyncThunk(
  "orders/fetchSubmittedVendorOrders",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get("/api/vendor-orders", { params: { rfq_no } });
      return response.data || [];
    } catch (error) {
      return rejectWithValue("Failed to load submitted orders");
    }
  }
);

export const fetchVendorFollowupHistory = createAsyncThunk(
  "orders/fetchVendorFollowupHistory",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get("/api/vendor-orders/followup-history", { params: { rfq_no } });
      return response.data?.history || [];
    } catch (error) {
      return rejectWithValue("Failed to load followup history");
    }
  }
);

export const fetchVendorProductOptions = createAsyncThunk(
  "orders/fetchVendorProductOptions",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const res = await axiosClientOrders.get("/api/vendor-orders/products", { params: { rfq_no } });
      return res.data.products || [];
    } catch (error) {
      return rejectWithValue("Failed to load products");
    }
  }
);

export const updateVendorFollowupEntry = createAsyncThunk(
  "orders/updateVendorFollowupEntry",
  async ({ srNo, followup_date, followup_remark }, { rejectWithValue }) => {
    try {
      await axiosClientOrders.put(`/api/vendor-followup/${srNo}`, { followup_date, followup_remark });
      return srNo;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deleteVendorFollowupEntry = createAsyncThunk(
  "orders/deleteVendorFollowupEntry",
  async (srNo, { rejectWithValue }) => {
    try {
      await axiosClientOrders.delete(`/api/vendor-followup/${srNo}`);
      return srNo;
    } catch (error) {
      return rejectWithValue("Failed to delete followup");
    }
  }
);

export const updateVendorOrderFollowup = createAsyncThunk(
  "orders/updateVendorOrderFollowup",
  async ({ orderId, payload }, { rejectWithValue }) => {
    try {
      await axiosClientOrders.put(`/api/vendor-orders/${orderId}/followup`, payload);
      return orderId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.details || error.response?.data?.error || error.response?.data?.message || error.message
      );
    }
  }
);

export const deleteVendorOrder = createAsyncThunk(
  "orders/deleteVendorOrder",
  async (orderId, { rejectWithValue }) => {
    try {
      await axiosClientOrders.delete(`/api/vendor-orders/${orderId}`);
      return orderId;
    } catch (error) {
      return rejectWithValue("Failed to delete order");
    }
  }
);

export const fetchVendorOrderById = createAsyncThunk(
  "orders/fetchVendorOrderById",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get(`/api/vendor-orders/${orderId}`);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load vendor order");
    }
  }
);

export const fetchVendorOrderPdf = createAsyncThunk(
  "orders/fetchVendorOrderPdf",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get(`/api/vendor-orders/${orderId}/pdf`, { responseType: "blob" });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchVendorOrderAttachments = createAsyncThunk(
  "orders/fetchVendorOrderAttachments",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get(`/api/vendor-orders/${orderId}/attachments`);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error("Failed to load attachments");
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load attachments");
    }
  }
);

export const fetchVendorOrderAttachmentUrl = createAsyncThunk(
  "orders/fetchVendorOrderAttachmentUrl",
  async ({ orderId, fileKey }, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get(
        `/api/vendor-orders/${orderId}/attachments/${encodeURIComponent(fileKey)}`
      );
      if (response.data.success) {
        return response.data.downloadUrl;
      }
      throw new Error("Failed to generate download link");
    } catch (error) {
      return rejectWithValue(error.message || "Failed to generate download link");
    }
  }
);

// ─── Vendor Order Form (quotation bundle + vendor terms + submission) ─────
// Mirrors the original fetchQuotationData() in VendorOrderForm.jsx: each
// sub-fetch is fault-isolated, only the initial material fetch can abort it.
export const fetchVendorOrderFormBundle = createAsyncThunk(
  "orders/fetchVendorOrderFormBundle",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const materialResponse = await axiosClientOrders.get("/api/order-quotation-material", {
        params: { rfq_no },
      });
      if (materialResponse.data.error) {
        throw new Error(materialResponse.data.error);
      }

      let vendorData = [];
      try {
        const vendorRes = await axiosClientOrders.get("/api/quotation-vendor", { params: { rfq_no } });
        vendorData = vendorRes.data;
      } catch (error) {
        console.error("Error fetching vendor data:", error);
      }

      let termsConditionData = [];
      try {
        const termsRes = await axiosClientOrders.get("/api/quotation-terms-condition", { params: { rfq_no } });
        termsConditionData = termsRes.data;
      } catch (error) {
        console.error("Error fetching terms conditions:", error);
      }

      let vendorOrderTerms = [];
      try {
        const votRes = await axiosClientOrders.get("/api/vendor-order-terms", { params: { rfq_no } });
        vendorOrderTerms = votRes.data || [];
      } catch (error) {
        console.error("Error fetching vendor order terms:", error);
      }

      let inquiryDetails = null;
      try {
        const inquiryRes = await axiosClient.get("/api/inquiry-details", { params: { rfq_no } });
        if (inquiryRes.data.length > 0) {
          inquiryDetails = inquiryRes.data[0];
        }
      } catch (error) {
        console.error("Error fetching contact details:", error);
      }

      let submittedOrders = [];
      try {
        const submittedRes = await axiosClientOrders.get("/api/vendor-orders", { params: { rfq_no } });
        submittedOrders = submittedRes.data || [];
      } catch (error) {
        console.error("Error fetching submitted orders:", error);
      }

      return {
        quotationMaterial: materialResponse.data,
        vendorData,
        termsConditionData,
        vendorOrderTerms,
        inquiryDetails,
        submittedOrders,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load quotation data");
    }
  }
);

export const saveVendorOrderTerms = createAsyncThunk(
  "orders/saveVendorOrderTerms",
  async (payload, { rejectWithValue }) => {
    try {
      await axiosClientOrders.post("/api/vendor-order-terms", payload);
      return payload.vendor_name;
    } catch (error) {
      return rejectWithValue("Failed to save vendor terms. Please try again.");
    }
  }
);

export const fetchAllVendorsList = createAsyncThunk(
  "orders/fetchAllVendorsList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/vendordata");
      return response.data || [];
    } catch (error) {
      return rejectWithValue("Failed to fetch vendors list");
    }
  }
);

export const fetchVendorCurrencyMap = createAsyncThunk(
  "orders/fetchVendorCurrencyMap",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get("/api/vendor-currency-map");
      return response.data || {};
    } catch (error) {
      return rejectWithValue("Failed to fetch vendor currency map");
    }
  }
);

export const fetchVendorContacts = createAsyncThunk(
  "orders/fetchVendorContacts",
  async (vendorId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/allvendordata/${vendorId}`, {
        params: { limit: 1000, offset: 0 },
      });
      return response.data?.data || response.data || [];
    } catch (error) {
      return rejectWithValue("Failed to fetch vendor contacts");
    }
  }
);

export const fetchNextVendorPoNo = createAsyncThunk(
  "orders/fetchNextVendorPoNo",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get("/api/vendor-orders/next-po-no");
      return response.data.po_no;
    } catch (error) {
      return rejectWithValue("Failed to fetch next PO number");
    }
  }
);

export const submitVendorOrder = createAsyncThunk(
  "orders/submitVendorOrder",
  async ({ isUpdate, vendorOrderId, formData }, { rejectWithValue }) => {
    try {
      const response = isUpdate
        ? await axiosClientOrders.put(`/api/vendor-orders/${vendorOrderId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
        : await axiosClientOrders.post("/api/vendor-orders", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const sendVendorOrderEmail = createAsyncThunk(
  "orders/sendVendorOrderEmail",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.post("/api/vendor-orders/send-email", payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// ─── Standalone quotation material fetch (TestQuotation.jsx) ──────────────
export const fetchQuotationMaterial = createAsyncThunk(
  "orders/fetchQuotationMaterial",
  async (rfq_no, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get("/api/order-quotation-material", {
        params: { rfq_no },
      });
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load quotation data");
    }
  }
);

// ─── Proforma Invoices (shared between ProformaForm.jsx and UpdateProformaForm.jsx) ─
// Next PI number: caller decides how to handle failure — ProformaForm shows an
// error, UpdateProformaForm silently falls back to "PI-0001".
export const fetchNextProformaPiNumber = createAsyncThunk(
  "orders/fetchNextProformaPiNumber",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get(`/api/proforma-invoices/next-pi-number/${orderId}`);
      if (response.data && response.data.success) {
        return response.data.pi_number;
      }
      return rejectWithValue(response.data?.error || "Failed to generate PI number");
    } catch (error) {
      return rejectWithValue(error.message || "Failed to generate PI number");
    }
  }
);

// List of proforma invoices for an order — used both for the invoices table
// (ProformaForm.jsx) and to derive defaults from the latest PI (UpdateProformaForm.jsx).
export const fetchProformaInvoicesForOrder = createAsyncThunk(
  "orders/fetchProformaInvoicesForOrder",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get(`/api/proforma-invoices/order/${orderId}`);
      if (response.data.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data.error || "Failed to fetch proforma invoices");
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch proforma invoices");
    }
  }
);

export const fetchProformaOrderFinancials = createAsyncThunk(
  "orders/fetchProformaOrderFinancials",
  async ({ orderId, rfqNo }, { rejectWithValue }) => {
    try {
      const [financialResponse, quotationTotalResponse] = await Promise.all([
        axiosClientOrders.get(`/api/proforma-invoices/order-financials/${orderId}`),
        rfqNo
          ? axiosClientOrders.get(`/api/quotation-total/${encodeURIComponent(rfqNo)}`)
          : Promise.resolve({ data: { success: false, data: {} } }),
      ]);
      return {
        financials: financialResponse.data,
        quotationTotal: quotationTotalResponse.data,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch proforma financials");
    }
  }
);

export const uploadProformaAttachment = createAsyncThunk(
  "orders/uploadProformaAttachment",
  async ({ invoiceId, file, onUploadProgress }, { rejectWithValue }) => {
    try {
      const submitData = new FormData();
      submitData.append("invoice_id", invoiceId);
      submitData.append("attachment", file);
      const response = await axiosClientOrders.post("/api/proforma-invoices/upload-attachment", submitData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to upload attachment");
    }
  }
);

export const uploadProformaPaymentReceipt = createAsyncThunk(
  "orders/uploadProformaPaymentReceipt",
  async ({ invoiceId, file }, { rejectWithValue }) => {
    try {
      const submitData = new FormData();
      submitData.append("invoice_id", invoiceId);
      submitData.append("payment_receipt", file);
      const response = await axiosClientOrders.post("/api/proforma-invoices/upload-payment-receipt", submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to upload payment receipt");
    }
  }
);

export const deleteProformaInvoice = createAsyncThunk(
  "orders/deleteProformaInvoice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.delete(`/api/proforma-invoices/${id}`);
      if (response.data.success) return id;
      return rejectWithValue(response.data.error || "Failed to delete proforma invoice");
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to delete proforma invoice");
    }
  }
);

// ─── UpdateProformaForm.jsx-specific fetches ───────────────────────────────
export const fetchProformaOrderTableData = createAsyncThunk(
  "orders/fetchProformaOrderTableData",
  async (orderId) => {
    try {
      const response = await axiosClientOrders.get(`/api/proforma-invoices/order-details/${orderId}`);
      if (response.data && response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching order table data:", error);
      return null;
    }
  }
);

export const fetchProformaMaterialsFromQuotation = createAsyncThunk(
  "orders/fetchProformaMaterialsFromQuotation",
  async (rfqNo) => {
    if (!rfqNo) return [];
    try {
      const response = await axiosClientOrders.get(`/api/proforma-invoices/materials/${rfqNo}`);
      if (response.data && response.data.success) {
        return response.data.data || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching materials:", error);
      return [];
    }
  }
);

export const fetchProformaClientInfo = createAsyncThunk(
  "orders/fetchProformaClientInfo",
  async (rfqNo) => {
    if (!rfqNo) return null;
    try {
      const response = await axiosClientOrders.get("/api/inquiry-details", {
        params: { rfq_no: rfqNo },
      });
      if (response.data && response.data.length > 0) {
        return response.data[0];
      }
      return null;
    } catch (error) {
      console.error("Error fetching client info:", error);
      return null;
    }
  }
);

export const uploadProformaPdf = createAsyncThunk(
  "orders/uploadProformaPdf",
  async ({ pdfBlob, piNumber, orderId }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      const pdfFile = new File([pdfBlob], `Proforma_${piNumber}.pdf`, {
        type: "application/pdf",
        lastModified: new Date().getTime(),
      });
      formData.append("pdf", pdfFile);
      formData.append("pi_number", piNumber);
      formData.append("order_id", orderId);
      const response = await axiosClientOrders.post("/api/proforma-invoices/upload-proforma-pdf", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      if (response.data && response.data.success) {
        return response.data.fileUrl || response.data.pdfUrl;
      }
      throw new Error(response.data?.error || "Failed to upload PDF");
    } catch (error) {
      return rejectWithValue(error.message || "Error uploading PDF");
    }
  }
);

// Like fetchSignedUrl, but also returns `ext` (needed by ProformaForm.jsx's file modal).
export const fetchProformaSignedUrl = createAsyncThunk(
  "orders/fetchProformaSignedUrl",
  async (fileUrl, { rejectWithValue }) => {
    try {
      const response = await axiosClientOrders.get("/api/proforma-invoices/signed-url", {
        params: { url: encodeURIComponent(fileUrl), expires_in: 3600 },
      });
      return { signedUrl: response.data.signedUrl, ext: response.data.ext };
    } catch (error) {
      return rejectWithValue(error.message || "Could not load file.");
    }
  }
);

export const saveProformaInvoice = createAsyncThunk(
  "orders/saveProformaInvoice",
  async ({ invoiceId, payload }, { rejectWithValue }) => {
    try {
      const response = invoiceId
        ? await axiosClientOrders.put(`/api/proforma-invoices/${invoiceId}`, payload)
        : await axiosClientOrders.post("/api/proforma-invoices", payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

const ordersSlice = createSlice({
  name: "orders",
  initialState: {
    orders: [],
    loading: false,
    isSearching: false,
    error: null,

    selectedOrder: null,
    selectedOrderLoading: false,

    actionOrderDetails: null,
    actionLoading: false,

    quotationTotal: null,
    fetchingTotal: false,

    saving: false,

    quotationLoading: false,
    poCopies: [],
    poCopiesLoading: false,

    orderDocs: [],
    orderDocsLoading: false,

    vendors: [],
    submittedOrders: [],
    followupHistory: [],
    productOptions: [],

    vendorOrderFormLoading: false,
  },
  reducers: {
    clearSelectedOrder: (state) => {
      state.selectedOrder = null;
    },
    clearActionOrderDetails: (state) => {
      state.actionOrderDetails = null;
    },
    clearProductOptions: (state) => {
      state.productOptions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchOrders
      .addCase(fetchOrders.pending, (state, action) => {
        state.loading = true;
        state.isSearching = !!action.meta.arg;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.isSearching = false;
        state.orders = action.payload;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.isSearching = false;
        state.orders = [];
        state.error = action.payload;
      })

      // fetchOrderById (OrderModelForm)
      .addCase(fetchOrderById.pending, (state) => {
        state.selectedOrderLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.selectedOrderLoading = false;
        state.selectedOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.selectedOrderLoading = false;
        state.error = action.payload;
      })

      // fetchOrderForAction (Order.jsx action modal)
      .addCase(fetchOrderForAction.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderForAction.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionOrderDetails = action.payload;
      })
      .addCase(fetchOrderForAction.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionOrderDetails = null;
        state.error = action.payload;
      })

      // createOrder / updateOrder
      .addCase(createOrder.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateOrder.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateOrder.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // deleteOrder
      .addCase(deleteOrder.rejected, (state, action) => {
        state.error = action.payload;
      })

      // fetchQuotationTotal
      .addCase(fetchQuotationTotal.pending, (state) => {
        state.fetchingTotal = true;
      })
      .addCase(fetchQuotationTotal.fulfilled, (state, action) => {
        state.fetchingTotal = false;
        state.quotationTotal = action.payload;
      })
      .addCase(fetchQuotationTotal.rejected, (state, action) => {
        state.fetchingTotal = false;
        state.quotationTotal = null;
        state.error = action.payload;
      })

      // fetchQuotationBundle (data returned via unwrap(), only track loading here)
      .addCase(fetchQuotationBundle.pending, (state) => {
        state.quotationLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotationBundle.fulfilled, (state) => {
        state.quotationLoading = false;
      })
      .addCase(fetchQuotationBundle.rejected, (state, action) => {
        state.quotationLoading = false;
        state.error = action.payload;
      })

      // fetchPoCopies
      .addCase(fetchPoCopies.pending, (state) => {
        state.poCopiesLoading = true;
      })
      .addCase(fetchPoCopies.fulfilled, (state, action) => {
        state.poCopiesLoading = false;
        state.poCopies = action.payload;
      })
      .addCase(fetchPoCopies.rejected, (state, action) => {
        state.poCopiesLoading = false;
        state.error = action.payload;
      })

      // deletePoCopy
      .addCase(deletePoCopy.rejected, (state, action) => {
        state.error = action.payload;
      })

      // fetchOrderDocs
      .addCase(fetchOrderDocs.pending, (state) => {
        state.orderDocsLoading = true;
      })
      .addCase(fetchOrderDocs.fulfilled, (state, action) => {
        state.orderDocsLoading = false;
        state.orderDocs = action.payload;
      })
      .addCase(fetchOrderDocs.rejected, (state) => {
        state.orderDocsLoading = false;
        state.orderDocs = [];
      })

      // Vendor table lists (loading/error stay local to the component, same
      // shared-flag pattern as the original — only the data lives in redux)
      .addCase(fetchVendorsList.fulfilled, (state, action) => {
        state.vendors = action.payload;
      })
      .addCase(fetchVendorsList.rejected, (state) => {
        state.vendors = [];
      })
      .addCase(fetchSubmittedVendorOrders.fulfilled, (state, action) => {
        state.submittedOrders = action.payload;
      })
      .addCase(fetchSubmittedVendorOrders.rejected, (state) => {
        state.submittedOrders = [];
      })
      .addCase(fetchVendorFollowupHistory.fulfilled, (state, action) => {
        state.followupHistory = action.payload;
      })
      .addCase(fetchVendorFollowupHistory.rejected, (state) => {
        state.followupHistory = [];
      })
      .addCase(fetchVendorProductOptions.fulfilled, (state, action) => {
        state.productOptions = action.payload;
      })
      .addCase(fetchVendorProductOptions.rejected, (state) => {
        state.productOptions = [];
      })

      // fetchVendorOrderFormBundle (data returned via unwrap(), only track loading here)
      .addCase(fetchVendorOrderFormBundle.pending, (state) => {
        state.vendorOrderFormLoading = true;
      })
      .addCase(fetchVendorOrderFormBundle.fulfilled, (state, action) => {
        state.vendorOrderFormLoading = false;
        state.submittedOrders = action.payload.submittedOrders;
      })
      .addCase(fetchVendorOrderFormBundle.rejected, (state) => {
        state.vendorOrderFormLoading = false;
      });
  },
});

export const { clearSelectedOrder, clearActionOrderDetails, clearProductOptions } = ordersSlice.actions;
export default ordersSlice.reducer;
